import { Button } from '@optools/components/ui/button';
import { Card } from '@optools/components/ui/card';
import { ScrollArea, ScrollBar } from '@optools/components/ui/scroll-area';
import { cn } from '@optools/components/ui/utils';
import { useCopyToClipboard } from '@optools/hooks/useCopyToClipboard';
import { Copy, CopyCheck } from 'lucide-react';
import { useLayoutEffect, useRef, useState } from 'react';
import JSonView from 'react18-json-view';
import { JsonValue } from 'type-fest';

import 'react18-json-view/src/style.css';

export interface JsonCodeBlockProps {
  className?: string;
  json: JsonValue;
  showCopyButton?: boolean;
}

export function JsonCodeBlock({ className, json, showCopyButton = true }: JsonCodeBlockProps) {
  const [copiedText, copy] = useCopyToClipboard();
  const jsonString = JSON.stringify(json, null, 2);

  const contentRef = useRef<HTMLDivElement | null>(null);
  const [initialDimensions, setInitialDimensions] = useState<{
    height: number;
    width: number;
  } | null>(null);

  useLayoutEffect(() => {
    const handleDimensions = () => {
      if (contentRef.current && !initialDimensions?.width && !initialDimensions?.height) {
        setInitialDimensions({
          height: contentRef.current.clientHeight,
          width: contentRef.current.clientWidth,
        });
      }
    };

    window.addEventListener('resize', handleDimensions);
    handleDimensions();

    return () => {
      window.removeEventListener('resize', handleDimensions);
    };
  }, [contentRef, initialDimensions]);

  return (
    <Card className={cn('group relative flex overflow-hidden p-2', className)}>
      <ScrollArea
        className="w-full overflow-auto pr-4 [&_[data-slot=scroll-area-scrollbar][data-orientation=vertical]_[data-slot=scroll-area-thumb]]:right-0"
        type="auto">
        <div
          ref={contentRef}
          className="text-xs"
          style={{
            minHeight: `min(${initialDimensions?.height}px, 75vh)`,
            minWidth:
              typeof initialDimensions?.width === 'number' ? initialDimensions.width + 16 : 'auto',
          }}>
          <JSonView
            className="[&_.json-view]:font-mono! [&_.json-view--bool]:text-purple-600! dark:[&_.json-view--bool]:text-purple-400! [&_.json-view--null]:text-purple-600! dark:[&_.json-view--null]:text-purple-400! [&_.json-view--number]:text-cyan-800! dark:[&_.json-view--number]:text-cyan-300! [&_.json-view--property]:text-amber-700! dark:[&_.json-view--property]:text-orange-300! [&_.json-view--string]:text-green-700! dark:[&_.json-view--string]:text-green-300! [&_.jv-size]:opacity-100!"
            collapsed={false}
            customizeCopy={async (node) => {
              await navigator.clipboard.writeText(JSON.stringify(node, null, 2));
            }}
            displaySize="collapsed"
            enableClipboard={true}
            src={json}
          />
        </div>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>
      {showCopyButton && (
        <Button
          className={cn(
            'absolute right-5 bottom-5 text-xs opacity-0 transition-opacity group-hover:opacity-100',
            copiedText && 'opacity-100',
          )}
          size="sm"
          variant="secondary"
          onClick={() => copy(jsonString)}>
          {copiedText ? (
            <>
              <CopyCheck />
              Copied
            </>
          ) : (
            <>
              <Copy /> Copy JSON
            </>
          )}
        </Button>
      )}
    </Card>
  );
}
